import Field from "@/components/Field";
import { Required } from "@/components/Required";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useWeekdays } from "@/modules/projects/hooks/useWeekdays";
import type { CreateProjectScheduleForm } from "@/types/projectSchedule";
import {
  SCHEDULE_FORM_WEEKDAY_KEYS,
  scheduleFormWeekdaysSchema,
} from "@/schemas/scheduleFormWeekdays";
import { isRequired } from "@/lib/form";
import { cn } from "@/lib/utils";
import { memo, useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

const ALL_WEEKDAYS_SELECTED = {
  Mo: true,
  Tu: true,
  We: true,
  Th: true,
  Fr: true,
  Sa: true,
  Su: true,
} as CreateProjectScheduleForm["weekdays"];

const ALL_WEEKDAYS_UNSELECTED = {
  Mo: false,
  Tu: false,
  We: false,
  Th: false,
  Fr: false,
  Sa: false,
  Su: false,
} as CreateProjectScheduleForm["weekdays"];

const WeekdaysFieldComponent = () => {
  const { t } = useTranslation();
  const { setValue, trigger, formState, clearErrors } =
    useFormContext<CreateProjectScheduleForm>();
  const { errors } = formState;
  const weekdays = useWeekdays();
  const weekdaysValue = useWatch({ name: "weekdays" });
  const weekdaysErrorMessage = errors.weekdays?.root?.message;

  const allWeekdaysSelected = useMemo(
    () =>
      !!weekdaysValue &&
      SCHEDULE_FORM_WEEKDAY_KEYS.every((k) => weekdaysValue[k]),
    [weekdaysValue],
  );

  const handleSelectAllWeekdaysChange = useCallback(
    (checked: boolean) => {
      setValue(
        "weekdays",
        checked ? ALL_WEEKDAYS_SELECTED : ALL_WEEKDAYS_UNSELECTED,
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
      clearErrors("weekdays");
    },
    [clearErrors, setValue],
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      <div className="flex w-full flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Label
          className={cn(
            "text-sm font-medium leading-none",
            weekdaysErrorMessage && "text-destructive",
          )}
        >
          {t("projectSchedules.fields.weekdays_label")}{" "}
          {isRequired(scheduleFormWeekdaysSchema) && <Required />}
        </Label>
        <div className="flex shrink-0 items-center gap-2">
          <Switch
            id="weekdays-select-all"
            checked={allWeekdaysSelected}
            onCheckedChange={handleSelectAllWeekdaysChange}
          />
          <Label htmlFor="weekdays-select-all" className="cursor-pointer">
            {t("projectSchedules.fields.weekdays_select_all")}
          </Label>
        </div>
      </div>
      <div className="grid w-full grid-cols-7 gap-x-1 gap-y-2 sm:gap-x-2">
        {weekdays.map((day) => (
          <div key={day.value} className="flex min-w-0 justify-center">
            <Field
              suppressValidationMessage
              name={`weekdays.${day.value}`}
              label={day.label}
              className="flex flex-row-reverse gap-2"
              render={({ field }) => (
                <Checkbox
                  data-testid={`weekday-checkbox-${day.value}`}
                  className="h-4 w-4 shrink-0"
                  checked={field.value}
                  onCheckedChange={(v) => {
                    field.onChange(v === true);
                    trigger("weekdays");
                  }}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              )}
            />
          </div>
        ))}
      </div>
      {weekdaysErrorMessage && (
        <p className="w-full text-sm font-medium text-destructive">
          {t(weekdaysErrorMessage)}
        </p>
      )}
    </div>
  );
};

export const WeekdaysField = memo(WeekdaysFieldComponent);
